import { createSelector } from '@ngrx/store';
import { selectOnline } from '@panamax/app-state';
import { Crumb } from '@shared/components/breadcrumbs/breadcrumbs.component';
import { AppStateForProduct } from '@shared/models/app-state-for-product';
import { Product } from '@shared/models/product.model';
import {
  appStateForProductsSelector,
  productWithAlternativesSelector,
} from '@shared/selectors/product.selectors';
import { AIList, AIListItem, JobStatus } from '@usf/list-ai-types';
import { ListKey } from '@usf/list-types';
import {
  IAIListFilterState,
  IAIListMenuSearchSortFilterOptions,
  IAIListSearchResultsState,
  IAIListState,
  selectAIListState,
  selectAllAiLists,
  selectFilterOptions,
  selectListSearchKey,
  selectMenuSearchSortFilterOptions,
  selectSearchResults,
  selectSearchResultsLoaded,
  selectSearchResultsLoading,
  selectSelectedAiList,
} from '@usf/ngrx-list-ai';
import {
  AIListBuilderViewModel,
  AIListIngredientMenuViewModel,
  FileParse,
  ProductMatch,
} from '../model/ai-list-builder-view.model';
import { AIListProductCardViewModel } from '../model/ai-list-product-card.model';
import { ListsViewModel } from '../model/lists-view.model';
import { productMatchesKeyword } from '../shared/list-detail-management/selectors/helpers/list-detail-management.selectors.helper';
import { isProductRestricted } from '@shared/selectors/helpers/product-info.selectors.helper';

export const selectAiListSearchResults = createSelector(
  selectSearchResults,
  selectMenuSearchSortFilterOptions,
  productWithAlternativesSelector,
  appStateForProductsSelector,
  (
    searchResultsState: IAIListSearchResultsState,
    menuOptions: IAIListMenuSearchSortFilterOptions,
    productMap: Map<number, Product>,
    appStateForProduct: AppStateForProduct,
  ): {
    filteredSearchResults: Product[];
    selectedSearchResult: Product | undefined;
    productNumbers: number[];
  } => {
    const selectedSearchResult = searchResultsState.selectedProduct
      ? productMap.get(Number(searchResultsState.selectedProduct))
      : undefined;

    const searchResults: Product[] =
      searchResultsState.searchResults?.products?.map(result => {
        const product = productMap.get(Number(result.productNumber));
        return product;
      });
    const productNumbers =
      searchResults?.map(result => result?.productNumber) || [];

    const filteredSearchResults: Product[] = filterSearchResults(
      searchResults,
      menuOptions?.filters || [],
    );
    const filteredSearchResultsWithoutRestrictedProducts: Product[] = (
      filteredSearchResults ?? []
    ).filter(
      product =>
        product &&
        !isProductRestricted(
          product,
          appStateForProduct,
          product.isMslRestricted,
        ),
    );
    return {
      filteredSearchResults: filteredSearchResultsWithoutRestrictedProducts,
      selectedSearchResult,
      productNumbers,
    };
  },
);

export const selectInitialAiListProductCardViewModel = createSelector(
  selectSearchResults,
  productWithAlternativesSelector,
  (
    searchResultState: IAIListSearchResultsState,
    productMap: Map<number, Product>,
  ): AIListProductCardViewModel => {
    const product = productMap.get(
      Number(searchResultState.initialItemSelection?.match.productNumber),
    );
    const ingredientDisplayName =
      searchResultState.initialItemSelection?.sourceText ===
      'Best Selling PIM Groups'
        ? product?.summary?.categoryDescription ||
          searchResultState.initialItemSelection?.ingredientName
        : searchResultState.initialItemSelection?.ingredientName;
    return {
      product: product,
      aiListItem: {
        ...searchResultState.initialItemSelection,
        ingredientIndex:
          searchResultState.initialItemSelection?.ingredientIndex,
        groupIndex: searchResultState.initialItemSelection?.groupIndex,
        groupName: searchResultState.initialItemSelection?.groupName,
      },
      ingredientDisplayName: ingredientDisplayName,
    } as AIListProductCardViewModel;
  },
);

export const selectAIListIngredientMenuViewModel = createSelector(
  selectInitialAiListProductCardViewModel,
  selectSearchResultsLoading,
  selectSearchResultsLoaded,
  selectAiListSearchResults,
  selectSelectedAiList,
  (
    initialAIListProductCardViewModel: AIListProductCardViewModel,
    searchResultsLoading: boolean,
    searchResultsLoaded: boolean,
    searchResults: {
      filteredSearchResults: Product[];
      selectedSearchResult: Product | undefined;
      productNumbers: number[];
    },
    selectedAiList: AIList,
  ) => {
    return {
      initialAIListProductCardViewModel,
      selectedSearchResult: searchResults.selectedSearchResult,
      loading: searchResultsLoading,
      loaded: searchResultsLoaded,
      searchResults: searchResults.filteredSearchResults,
      productNumbers: searchResults.productNumbers, // We need this to get the loading state of the products
      selectedAiList,
    } as AIListIngredientMenuViewModel;
  },
);

/**
 * Filters the raw search results based on menu filter options and search key
 * Performs filtering on the pre filtered Products before returning further filtered Products
 *
 * @param alternativeProducts Array of raw search result items
 * @param filters Array of filter IDs that are active
 * @param searchKey Search text to filter by
 * @returns Filtered array of Products
 */
export const filterSearchResults = (
  searchResults: Product[],
  filters: string[],
): Product[] => {
  if (!filters || filters.length === 0) {
    return searchResults;
  }

  return searchResults?.filter(searchResult => {
    if (filters && filters.length > 0) {
      const filterConditions = {
        'direct': searchResult?.inventory?.isDirect === true,
        'early-cutoff': searchResult?.inventory?.justInTime === true,
        'discontinued': searchResult?.inventory?.productStatus === '8',
        'exclusive': searchResult?.summary?.isExclusiveBrand === true,
      };

      const passesFilter = filters.some(
        filter => filterConditions[filter as keyof typeof filterConditions],
      );

      if (!passesFilter) {
        return false;
      }
    }

    return true;
  });
};

export const selectAIListBuilderViewModel = createSelector(
  selectSelectedAiList,
  selectOnline(),
  productWithAlternativesSelector,
  selectFilterOptions,
  selectListSearchKey,
  selectAIListState,
  (
    aiList: AIList,
    isOnline: boolean,
    productMap: Map<number, Product>,
    filterState: IAIListFilterState,
    searchKey: string,
    aiListState: IAIListState,
  ): AIListBuilderViewModel => {
    const items = getAIListItemsFromList(aiList, productMap);
    // Item heights for all platforms and best selling products flow is 221.2, this will soon change to a couple different heights
    // that will have more logic involved once we start showing product data and what file the product was found in.

    const showSearchAndFilter = items.some(item => !item.loading);

    const filteredItems: AIListProductCardViewModel[] = filterItems(
      items,
      filterState,
      searchKey,
    );

    // Calculate heights for filtered items only, so virtual scroll total height matches displayed content
    const itemHeights: number[] = filteredItems.map(
      (item: AIListProductCardViewModel) => setHeight(item),
    );

    const filterOptions = Object.entries(filterState).map(([key, value]) => {
      let label = '';
      switch (key) {
        case 'direct':
          label = 'Direct';
          break;
        case 'earlyCutoff':
          label = 'Early Cutoff';
          break;
        case 'discontinuedWhenOut':
          label = 'Discontinued When Out';
          break;
        case 'exclusiveBrands':
          label = 'Exclusive Brands';
          break;
      }
      return {
        id: key,
        label,
        selected: value,
      };
    });

    let selectedFilterCount = 0;
    for (const option of filterOptions) {
      if (option.selected) {
        selectedFilterCount++;
      }
    }

    return {
      aiListId: aiList?.aiListId,
      aiListName: aiList?.aiListName,
      breadcrumbs: getAiListBuilderBreadCrumbs(),
      ingredientPreference: aiList?.menuProductType,
      numberOfFilesUploaded: aiList?.sourceFiles?.length ?? 0,
      menuType: aiList?.menuType,
      numberOfProducts: items?.length,
      items: filteredItems,
      isOnline,
      itemHeights,
      fileParse: selectFileParseForSelectedAiList(aiList),
      productMatch: selectProductMatchForSelectedAiList(aiList),
      updatedByUser: aiList?.updatedByUser,
      filterOptions,
      selectedFilterCount,
      showSearchAndFilter,
      updateDtm: aiList?.updateDtm,
    } as AIListBuilderViewModel;
  },
);

export const selectAiListsAsListViewModels = createSelector(
  selectAllAiLists,
  selectOnline(),
  (aiLists: AIList[]) => {
    const aiListViewModels: ListsViewModel[] = [];
    aiLists.forEach(aiList => {
      const items = getAIListItemsFromList(aiList);
      aiListViewModels.push({
        listKey: {
          listId: undefined,
          listTypeId: 'AI',
        } as ListKey,
        r4TempListId: undefined,
        listName: aiList.aiListName,
        divisionNumber: aiList.divisionNumber,
        customerNumber: aiList.customerNumber,
        departmentNumber: 0,
        listState: aiList.listState,
        effDate: '',
        endDate: '',
        listItemCount: items.length,
        discontinuedCount: 0,
        listGroupKeys: undefined,
        deleteDtm: '',
        updatedByUser: aiList.updatedByUser,
        isEnrichment: false,
        addUserIdSourceSystem: aiList.createdByUser?.userName,
        addDtmSourceSystem: aiList.addDtm,
        addSourceSystem: aiList.sourceSystem,
        updateUserIdSourceSystem: aiList.updatedByUser?.userName,
        updateDtmSourceSystem: aiList.updateDtm,
        updateSourceSystem: aiList.sourceSystem,
        version: '',
        aiListId: aiList.aiListId,
        showEllipsis: true,
      } as ListsViewModel);
    });
    return aiListViewModels;
  },
);

// Logic for this may change after meeting on 3/13/25
export const selectFileParseForSelectedAiList = (aiList: AIList): FileParse => {
  return {
    isParsingFiles:
      aiList?.parsed === false &&
      aiList?.parseJobStates?.length > 0 &&
      aiList?.sourceFiles?.length > 0,
    fileBeingParsedDisplayText: getFileBeingParsedDisplayText(aiList),
  } as FileParse;
};

export const selectProductMatchForSelectedAiList = (
  aiList: AIList,
): ProductMatch => {
  const numberOfProductsMatched =
    aiList?.groups?.reduce((acc, group) => {
      return (
        acc +
        (group?.groupItems?.filter(item => !!item?.match?.productNumber)
          ?.length || 0)
      );
    }, 0) || 0;

  return {
    isMatchingProducts:
      !!aiList?.matchJobState?.jobId &&
      (aiList?.matchJobState?.jobStatus === JobStatus.PROCESSING ||
        aiList?.matchJobState?.jobStatus === JobStatus.RETRYING),
    numberOfProductsMatched,
  };
};

const getFileBeingParsedDisplayText = (aiList: AIList): string => {
  let stringToReturn = '';
  // Count the number of completed parse jobs
  const numberOfFilesParsed =
    aiList?.parseJobStates?.filter(job => job.jobStatus === 'COMPLETED')
      ?.length || 0;
  const totalFiles = aiList?.sourceFiles?.length || 0;

  // If we haven't parsed all files yet, show the next ordinal number
  if (numberOfFilesParsed < totalFiles) {
    return numberToDisplayTextMap.get(numberOfFilesParsed + 1);
  }
  return stringToReturn;
};

const numberToDisplayTextMap = new Map([
  [1, 'first'],
  [2, 'second'],
  [3, 'third'],
  [4, 'fourth'],
  [5, 'fifth'],
  [6, 'sixth'],
  [7, 'seventh'],
  [8, 'eighth'],
  [9, 'ninth'],
]);

const getAiListBuilderBreadCrumbs = (): Crumb[] => {
  return [
    {
      label: 'i18n.breadcrumbs.home',
      url: '/desktop/home',
      active: false,
    },
    {
      label: 'i18n.breadcrumbs.myLists',
      url: '/desktop/lists',
      active: false,
    },
    {
      label: 'AI List Builder',
      url: '',
      active: true,
    },
  ];
};

const getAIListItemsFromList = (
  aiList: AIList,
  productMap?: Map<number, Product>,
): AIListProductCardViewModel[] => {
  let items: AIListProductCardViewModel[] = [];
  let lineNumber = 1;

  const isParsingCompleted = aiList?.parsed ?? false;

  if (aiList?.groups?.length > 0) {
    for (let groupIndex = 0; groupIndex < aiList.groups.length; groupIndex++) {
      const group = aiList.groups[groupIndex];
      if (group?.groupItems?.length > 0) {
        for (
          let ingredientIndex = 0;
          ingredientIndex < group.groupItems.length;
          ingredientIndex++
        ) {
          const item = group.groupItems[ingredientIndex];
          const itemProductCardViewModel = createAIListProductCardViewModel(
            item,
            lineNumber,
            productMap,
            isParsingCompleted,
            ingredientIndex,
            groupIndex,
            group.groupName,
            aiList,
          );
          items.push(itemProductCardViewModel);
          lineNumber++;
        }
      }
    }
  }
  return items;
};

const createAIListProductCardViewModel = (
  item: AIListItem,
  index: number,
  productMap: Map<number, Product>,
  isParsingCompleted: boolean,
  ingredientIndex: number,
  groupIndex: number,
  groupName: string,
  aiList?: AIList,
): AIListProductCardViewModel => {
  const hasProductNumber = !!item?.match?.productNumber;
  const product = hasProductNumber
    ? productMap?.get(Number(item?.match?.productNumber))
    : undefined;
  const sourceFileName = removeDirectoryFromPath(item?.sourceFileName);

  const { loaded, isMatchingFailed } = determineItemState(
    hasProductNumber,
    product,
    isParsingCompleted,
    productMap,
    aiList,
  );
  // Compute the ingredient display name based on the business logic
  const ingredientDisplayName =
    item?.sourceText === 'Best Selling PIM Groups'
      ? product?.summary?.categoryDescription || item?.ingredientName
      : item?.ingredientName;

  return {
    product: product,
    aiListItem: {
      ...item,
      ingredientIndex: ingredientIndex,
      groupIndex: groupIndex,
      groupName: groupName,
    },
    sourceFileDisplayText:
      item?.sourceFileName === 'Best Selling PIM Groups'
        ? 'i18n.lists.bestSellingProducts'
        : sourceFileName,
    loading: !loaded,
    lineNumber: index,
    isMatchingFailed: isMatchingFailed,
    ingredientDisplayName: ingredientDisplayName,
  } as AIListProductCardViewModel;
};

/**
 * Determines the loading and matching state of an AI list item
 */
const determineItemState = (
  hasProductNumber: boolean,
  product: Product | undefined,
  isParsingCompleted: boolean,
  productMap: Map<number, Product>,
  aiList?: AIList,
): { loaded: boolean; isMatchingFailed: boolean } => {
  if (hasProductNumber) {
    // Item has a product number, so matching was attempted for this specific item
    if (product) {
      // Product was found in the map
      if (product.notFound) {
        // Product is explicitly marked as not found
        return { loaded: true, isMatchingFailed: true };
      } else {
        // Product was found successfully
        return { loaded: true, isMatchingFailed: false };
      }
    } else {
      // Product not in map - could be still loading or failed to load
      // Conservative approach - show loading until we're sure it failed
      return { loaded: false, isMatchingFailed: false };
    }
  } else {
    // No product number
    if (isParsingCompleted) {
      // Parsing is complete but no product number - check if matching job is also complete
      const isMatchingCompleted =
        aiList?.matchJobState?.jobStatus === 'COMPLETED';

      if (isMatchingCompleted) {
        // Both parsing and matching are complete, but no product number - matching failed
        return { loaded: true, isMatchingFailed: true };
      } else {
        // Parsing complete but matching still in progress - show loading
        return { loaded: false, isMatchingFailed: false };
      }
    } else {
      // Parsing not complete - still being processed
      return { loaded: false, isMatchingFailed: false };
    }
  }
};

export function removeDirectoryFromPath(path: string): string {
  return path.split('/').pop() || path;
}

export const setHeight = (item: AIListProductCardViewModel): number => {
  // Loading scenario: items that are still being processed/matched
  // Height: 207.2px
  if (item.loading) {
    return 207.2;
  }

  // Product failed scenario: items that failed to load/match
  // Height: 207.2px
  if (item.isMatchingFailed) {
    return 207.2;
  }

  // Regular loaded scenario: items that have loaded successfully with no drawer
  // Height: 227.81px
  let baseHeight = 227.81;
  let offset = 0;

  // Card contains "found in" text
  // Add 19.61px
  if (
    item?.aiListItem?.sourceText &&
    item.aiListItem.sourceText !== 'Best Selling PIM Groups'
  ) {
    offset += 19.61;
  }

  // Black one-line-capitalize missing but has unable to order drawer scenario
  // Height: 249.2px
  if (
    !item?.product?.summary?.categoryDescription &&
    (item?.product?.drawerMsg?.message ||
      item?.product?.productCardWarningMsg?.display)
  ) {
    return 249.2;
  }

  return baseHeight + offset;
};

export const filterItems = (
  items: AIListProductCardViewModel[],
  filterState: IAIListFilterState,
  searchKey: string,
): AIListProductCardViewModel[] => {
  return items.filter(item => {
    // Include loading items and failed matches so they show up
    if (item.loading || item.isMatchingFailed) {
      return true;
    }
    return (
      isInFilter(item, filterState) &&
      enhancedProductMatchesKeyword(searchKey, item)
    );
  });
};

/**
 * Checks if a product matches the active filters by using filterSearchResults.
 * This function converts the filter state to the format expected by filterSearchResults
 * and uses it to check if the product matches any of the active filters.
 *
 * @param item - The AIListProductCardViewModel containing the product to check
 * @param filterState - The current state of filters (earlyCutoff, discontinuedWhenOut, direct, exclusiveBrands)
 * @returns true if no filters are active or if the product matches any active filter
 */
export const isInFilter = (
  item: AIListProductCardViewModel,
  filterState: IAIListFilterState,
) => {
  // If no filters are active, return true
  if (!Object.values(filterState).some(value => value)) {
    return true;
  }

  // Convert filterState to array of active filters
  const activeFilters = Object.entries(filterState)
    .filter(([_, isActive]) => isActive)
    .map(([key]) => {
      // Map the filter state keys to the filter names used in filterSearchResults
      switch (key) {
        case 'earlyCutoff':
          return 'early-cutoff';
        case 'discontinuedWhenOut':
          return 'discontinued';
        case 'direct':
          return 'direct';
        case 'exclusiveBrands':
          return 'exclusive';
        default:
          return key;
      }
    });

  // Use filterSearchResults to check if the product matches the filters
  const matchesFilters =
    filterSearchResults([item.product], activeFilters).length > 0;

  return matchesFilters;
};

/**
 * Enhanced keyword matching function that combines the search capabilities
 * of productMatchesKeyword with additional card-specific search fields.
 */
export const enhancedProductMatchesKeyword = (
  keyword: string,
  item: AIListProductCardViewModel,
): boolean => {
  if (!keyword || keyword === '') return true;

  // First try productMatchesKeyword
  const matchesProduct = productMatchesKeyword(
    keyword,
    item.product?.summary,
    undefined,
    item.product?.customerProductNumber,
  );

  if (matchesProduct) return true;

  // If no match found in main product, check card-specific fields
  const searchKey = keyword.toLowerCase();

  // Search in category description (title)
  const categoryDescription =
    item?.product?.summary?.categoryDescription?.toLowerCase();
  if (categoryDescription?.includes(searchKey)) return true;

  return false;
};
